/**
 * 城市管理页面
 */

import React, { useEffect, useState } from 'react';
import {
  Card,
  Table,
  Button,
  Modal,
  Form,
  Input,
  Select,
  message,
  Popconfirm,
  Space,
  Tag,
  Alert,
  Row,
  Col,
  Statistic,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  ReloadOutlined,
  ExportOutlined,
  ClearOutlined,
} from '@ant-design/icons';
import { ColumnsType } from 'antd/es/table';
import { City, CityFormData, PaginationParams } from '../../types';
import { getCities, createCity, getCityLevelStats, deleteAllCities, exportCities } from '../../services/api';
import { getCityLevelLabel, formatDateTime } from '../../utils/helpers';
import { exportCitiesToExcel, showConfirmDialog } from '../../utils/exportUtils';
import { CITY_LEVEL_OPTIONS } from '../../utils/constants';

const { Option } = Select;

const CitiesManagement: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [cities, setCities] = useState<City[]>([]);
  const [total, setTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [searchText, setSearchText] = useState('');
  const [selectedLevel, setSelectedLevel] = useState<number | undefined>();
  
  // 统计数据状态
  const [levelStats, setLevelStats] = useState<Array<{ level: number; count: number }>>([]);
  const [statsLoading, setStatsLoading] = useState(false);
  
  // 模态框状态
  const [modalVisible, setModalVisible] = useState(false);
  const [editingCity, setEditingCity] = useState<City | null>(null);
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    loadCities();
    loadLevelStats();
  }, [currentPage, pageSize, searchText, selectedLevel]); // eslint-disable-line react-hooks/exhaustive-deps

  // 加载城市列表
  const loadCities = async () => {
    try {
      setLoading(true);
      const params: PaginationParams = {
        page: currentPage,
        limit: pageSize,
        search: searchText || undefined,
        level: selectedLevel,
      };
      
      const data = await getCities(params);
      setCities(data.data);
      setTotal(data.pagination.total);
    } catch (err) {
      console.error('加载城市列表失败:', err);
      message.error(err instanceof Error ? err.message : '加载城市列表失败');
    } finally {
      setLoading(false);
    }
  };

  // 加载统计数据
  const loadLevelStats = async () => {
    try {
      setStatsLoading(true);
      const data = await getCityLevelStats();
      setLevelStats(data);
    } catch (err) {
      console.error('加载统计数据失败:', err);
      message.error(err instanceof Error ? err.message : '加载统计数据失败');
    } finally {
      setStatsLoading(false);
    }
  };

  // 处理搜索
  const handleSearch = (value: string) => {
    setSearchText(value);
    setCurrentPage(1);
  };

  // 处理等级筛选
  const handleLevelFilter = (value: number | undefined) => {
    setSelectedLevel(value);
    setCurrentPage(1);
  };

  // 打开新增模态框
  const handleAdd = () => {
    setEditingCity(null);
    setModalVisible(true);
    form.resetFields();
  };

  // 打开编辑模态框
  const handleEdit = (city: City) => {
    setEditingCity(city);
    setModalVisible(true);
    form.setFieldsValue({
      name: city.name,
      level: city.level,
    });
  };

  // 提交表单
  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setSubmitting(true);

      if (editingCity) {
        // TODO: 实现编辑功能
        message.info('编辑功能开发中...');
      } else {
        // 批量新增城市
        const cityNames = values.name.split('、').map((name: string) => name.trim()).filter(Boolean);
        
        if (cityNames.length === 0) {
          message.error('请输入至少一个城市名称');
          return;
        }

        let successCount = 0;
        let failedCities: string[] = [];

        for (const cityName of cityNames) {
          try {
            const formData: CityFormData = {
              name: cityName,
              level: values.level,
            };
            
            await createCity(formData);
            successCount++;
          } catch (err) {
            console.error(`创建城市 ${cityName} 失败:`, err);
            failedCities.push(cityName);
          }
        }

        if (successCount > 0) {
          message.success(`成功创建 ${successCount} 个城市`);
          if (failedCities.length > 0) {
            message.warning(`以下城市创建失败: ${failedCities.join('、')}`);
          }
          setModalVisible(false);
          loadCities();
          loadLevelStats();
        } else {
          message.error('所有城市创建失败，请检查输入');
        }
      }
    } catch (err) {
      console.error('提交失败:', err);
      if (err instanceof Error) {
        message.error(err.message);
      }
    } finally {
      setSubmitting(false);
    }
  };

  // 删除全部城市
  const handleDeleteAll = async () => {
    try {
      const confirmed = await showConfirmDialog(
        '确定要删除所有城市数据吗？\n此操作不可恢复，且如果有培训标准使用了城市等级，将无法删除。'
      );
      
      if (!confirmed) {
        return;
      }

      const result = await deleteAllCities();
      message.success(`已删除 ${result.deleted} 条城市记录`);
      loadCities();
      loadLevelStats();
    } catch (err) {
      console.error('删除全部城市失败:', err);
      message.error(err instanceof Error ? err.message : '删除全部城市失败');
    }
  };

  // 导出城市数据
  const handleExport = async () => {
    try {
      const allCities = await exportCities();
      exportCitiesToExcel(allCities, `城市数据_${new Date().toLocaleDateString()}.xlsx`);
      message.success('城市数据导出成功');
    } catch (err) {
      console.error('导出城市数据失败:', err);
      message.error(err instanceof Error ? err.message : '导出城市数据失败');
    }
  };

  // 删除城市
  const handleDelete = async (city: City) => {
    try {
      // TODO: 实现删除功能
      message.info('删除功能开发中...');
    } catch (err) {
      console.error('删除失败:', err);
      message.error(err instanceof Error ? err.message : '删除失败');
    }
  };

  // 表格列定义
  const columns: ColumnsType<City> = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
    },
    {
      title: '城市名称',
      dataIndex: 'name',
      key: 'name',
      width: 150,
      render: (text: string) => <strong>{text}</strong>,
    },
    {
      title: '城市等级',
      dataIndex: 'level',
      key: 'level',
      width: 120,
      render: (level: number) => {
        const colors = ['', 'red', 'orange', 'blue', 'green', 'purple'];
        return (
          <Tag color={colors[level]} key={level}>
            {getCityLevelLabel(level)}
          </Tag>
        );
      },
      filters: CITY_LEVEL_OPTIONS.map(option => ({
        text: option.label,
        value: option.value,
      })),
      onFilter: (value, record) => record.level === value,
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 180,
      render: (date: string) => formatDateTime(date),
      sorter: (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
    },
    {
      title: '操作',
      key: 'action',
      width: 120,
      fixed: 'right',
      render: (_, record: City) => (
        <Space>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
            size="small"
          >
            编辑
          </Button>
          <Popconfirm
            title="确定要删除这个城市吗？"
            description="删除后将无法恢复"
            onConfirm={() => handleDelete(record)}
            okText="确定"
            cancelText="取消"
          >
            <Button
              type="link"
              danger
              icon={<DeleteOutlined />}
              size="small"
            >
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  // 获取统计数据映射
  const getStatsData = () => {
    const totalCount = levelStats.reduce((sum, item) => sum + item.count, 0);
    const statsMap: { [key: number]: number } = {};
    levelStats.forEach(item => {
      statsMap[item.level] = item.count;
    });
    
    return {
      total: totalCount,
      level1: statsMap[1] || 0,
      level2: statsMap[2] || 0,
      level3: statsMap[3] || 0,
      level4: statsMap[4] || 0,
      level5: statsMap[5] || 0,
    };
  };

  const statsData = getStatsData();

  return (
    <div>

      {/* 统计卡片 */}
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col span={4}>
          <Card>
            <Statistic
              title="总城市数"
              value={statsData.total}
              loading={statsLoading}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col span={4}>
          <Card>
            <Statistic
              title="一线城市"
              value={statsData.level1}
              loading={statsLoading}
              valueStyle={{ color: '#f5222d' }}
            />
          </Card>
        </Col>
        <Col span={4}>
          <Card>
            <Statistic
              title="二线城市"
              value={statsData.level2}
              loading={statsLoading}
              valueStyle={{ color: '#fa8c16' }}
            />
          </Card>
        </Col>
        <Col span={4}>
          <Card>
            <Statistic
              title="三线城市"
              value={statsData.level3}
              loading={statsLoading}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col span={4}>
          <Card>
            <Statistic
              title="四线城市"
              value={statsData.level4}
              loading={statsLoading}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col span={4}>
          <Card>
            <Statistic
              title="五线城市"
              value={statsData.level5}
              loading={statsLoading}
              valueStyle={{ color: '#722ed1' }}
            />
          </Card>
        </Col>
      </Row>

      <Card>
        {/* 工具栏 */}
        <div style={{ marginBottom: 16 }}>
          <Space>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleAdd}
            >
              新增城市
            </Button>
            <Button
              icon={<ReloadOutlined />}
              onClick={() => {
                loadCities();
                loadLevelStats();
              }}
              loading={loading}
            >
              刷新
            </Button>
            <Button
              icon={<ExportOutlined />}
              onClick={handleExport}
            >
              导出数据
            </Button>
            <Popconfirm
              title="删除确认"
              description="确定要删除所有城市数据吗？此操作不可恢复！"
              onConfirm={handleDeleteAll}
              okText="确定"
              cancelText="取消"
              okButtonProps={{ danger: true }}
            >
              <Button
                danger
                icon={<ClearOutlined />}
              >
                删除全部
              </Button>
            </Popconfirm>
            <Select
              placeholder="筛选城市等级"
              allowClear
              style={{ width: 150 }}
              value={selectedLevel}
              onChange={handleLevelFilter}
            >
              {CITY_LEVEL_OPTIONS.map(option => (
                <Option key={option.value} value={option.value}>
                  {option.label}
                </Option>
              ))}
            </Select>
            <Input.Search
              placeholder="搜索城市名称..."
              allowClear
              style={{ width: 300 }}
              onSearch={handleSearch}
            />
          </Space>
        </div>

        {/* 表格 */}
        <Table
          columns={columns}
          dataSource={cities}
          rowKey="id"
          loading={loading}
          scroll={{ x: 1000 }}
          pagination={{
            current: currentPage,
            pageSize: pageSize,
            total: total,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) =>
              `第 ${range[0]}-${range[1]} 条，共 ${total} 条记录`,
            onChange: (page, size) => {
              setCurrentPage(page);
              if (size !== pageSize) {
                setPageSize(size);
                setCurrentPage(1);
              }
            },
          }}
        />
      </Card>

      {/* 新增/编辑模态框 */}
      <Modal
        title={editingCity ? '编辑城市' : '新增城市'}
        open={modalVisible}
        onOk={handleSubmit}
        onCancel={() => setModalVisible(false)}
        confirmLoading={submitting}
        width={600}
        destroyOnClose
      >
        <Form
          form={form}
          layout="vertical"
          initialValues={{
            level: 1,
          }}
        >
          <Form.Item
            name="level"
            label="城市等级"
            rules={[{ required: true, message: '请选择城市等级' }]}
          >
            <Select placeholder="请选择城市等级">
              {CITY_LEVEL_OPTIONS.map(option => (
                <Option key={option.value} value={option.value}>
                  {option.label}
                </Option>
              ))}
            </Select>
          </Form.Item>

          {editingCity ? (
            // 编辑模式 - 单个城市
            <>
              <Form.Item
                name="name"
                label="城市名称"
                rules={[
                  { required: true, message: '请输入城市名称' },
                  { max: 50, message: '城市名称不能超过50个字符' },
                ]}
              >
                <Input placeholder="请输入城市名称" />
              </Form.Item>

            </>
          ) : (
            // 新增模式 - 批量输入
            <Form.Item
              name="name"
              label="批量添加城市"
              rules={[
                { required: true, message: '请输入城市名称' },
              ]}
            >
              <Input.TextArea
                rows={4}
                placeholder="请输入城市名称，多个城市使用 、 分隔
例如：北京、上海、广州、深圳、杭州、南京、临沂、西宁"
                maxLength={1000}
                showCount
              />
            </Form.Item>
          )}
        </Form>

        <Alert
          message={editingCity ? "编辑说明" : "批量添加说明"}
          description={
            editingCity ? (
              <ul style={{ marginBottom: 0, paddingLeft: 20 }}>
                <li>编辑单个城市的基本信息</li>
                <li>城市等级用于培训标准的差异化管理</li>
              </ul>
            ) : (
              <ul style={{ marginBottom: 0, paddingLeft: 20 }}>
                <li><strong>批量输入：</strong>支持同时添加多个相同等级的城市</li>
                <li><strong>分隔符：</strong>使用中文顿号 "、" 分隔多个城市名称</li>
                <li><strong>城市名称：</strong>输入什么名称就保存什么名称，用于后期数据匹配</li>
                <li><strong>等级说明：</strong>一线城市(北上广深)、二线城市(省会)、三线城市(地级市)、四线城市(县城)</li>
                <li><strong>示例输入：</strong>北京、上海、广州、深圳、杭州、南京、临沂、西宁</li>
              </ul>
            )
          }
          type="info"
          showIcon
          style={{ marginTop: 16 }}
        />
      </Modal>
    </div>
  );
};

export default CitiesManagement;